import { SimpleTime } from '../models/simple-time';
const PIPE_REGEX = /\|([^|]+$)/;
const ARGUMENT_REGEX = /{{[^}]+}}/g; //Looks for {{someText}}
//Note: just because something passes the regex here does not mean that it is a valid time format.
//index 5 of the RegExpExecArray contains what the user typed for the localized Am/Pm string.
//That needs to be send through the isLocalizedAmPm() function to finish the validation.
const AM_PM_TIME_REGEX = /^ *(0?[1-9]|10|11|12) *: *([0-5]\d)( *: *([0-5]\d))? *(.*) *$/;
const TWENTY_FOUR_HOUR_TIME_REGEX = /^ *(0?[0-9]|1[0-9]|2[0-3]) *: *([0-5]\d)( *: *([0-5]\d))? *$/;
const TWO_DIGIT_REGEX = /^\d{2}$/;
/**
 * Copies the key/value pairs of message keys into the global translation map for
 * everything using the translate() function.
 *
 * @param map - key: the message key string.  value: the message that the translate function will return.
 * @param onlyIfEmpty - Set this to true if you don't want to overwrite what is already inside the global map.
 * This is good to set to true when setting default English message keys.
 */
export function setTranslations(map, onlyIfEmpty) {
    const messageKeys = getCurrentTranslations();
    for (const key of Object.keys(map)) {
        if (!onlyIfEmpty || !keyExists(key)) {
            messageKeys[key] = map[key];
        }
    }
}
/**
 * Returns the message keys that are currently in use by the application
 *
 * @example Here is an example return value
 * {
 *    'neon|example-key':'example-translation-key-value',
 *    'neon|example-key-2':'example-translation-key-value-2'
 * }
 */
export function getCurrentTranslations() {
    return getI18nObject().messageKeys;
}
export function getCurrentLocale() {
    return getI18nObject().locale;
}
/**
 *
 *
 * In order to get the neon components to localize on something other than the default,
 * you need to set a new Locale object. This will be done globally for ALL neon components
 * on the screen. The should typically be done at the application level and all neon components
 * inside MFEs injected into the application will use the new locale.
 * @param locale - The locale you want neon components to use.
 * @example Here is an example Locale
 * {
 *   'LOCALE': 'en_US',
 *   'DATETIME_FORMATS': {
 *     'MONTH': ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
 *     'SHORTMONTH': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
 *     'medium': 'MM/dd/yyyy hh:mm:ss a',
 *     'mediumDateText': 'MM/DD/YYYY',
 *     'shortDateText': 'MM/DD/YY',
 *     'AMPMS': ['AM', 'PM'],
 *     'mediumDate': 'MM/dd/yyyy',
 *     'mediumTime': 'hh:mm:ss a',
 *     'shortTime': 'hh:mm a',
 *     'short': 'M/d/yy hh:mm a',
 *     'SHORTDAY': ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
 *     'DAY': ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
 *     'shortDate': 'M/d/yy'
 *   },
 *   '$BREACH_MITIGATION': 'XRFmEENA55t627cN',
 *   'NUMBER_FORMATS': {
 *     'DECIMAL_SEP': '.',
 *     'number_regex': '([-]?(([0-9]{1,3}(,[0-9]{3})*(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))',
 *     'CURRENCY_SYM': '$',
 *     'GROUP_SEP': ',',
 *     'number_format': '#,###.#########'
 *   }
 * }
 * @deprecated Please use the setLocaleFormats() function instead.
 */
export function setCurrentLocale(locale) {
    getI18nObject().locale = locale;
    fireLocalChangeEvent();
}
/**
 * Copies the attributes in localeFormats defined in the LocaleFormats into the currentLocale.
 * @param localeFormats The locale format information to copy into the currentLocale.
 */
export function setLocaleFormats(localeFormats) {
    const locale = getCurrentLocale();
    locale.LOCALE = localeFormats.LOCALE;
    locale.DATETIME_FORMATS.medium = localeFormats.DATETIME_FORMATS.medium;
    locale.DATETIME_FORMATS.mediumDateText = localeFormats.DATETIME_FORMATS.mediumDateText;
    locale.DATETIME_FORMATS.shortDateText = localeFormats.DATETIME_FORMATS.shortDateText;
    locale.DATETIME_FORMATS.mediumDate = localeFormats.DATETIME_FORMATS.mediumDate;
    locale.DATETIME_FORMATS.mediumTime = localeFormats.DATETIME_FORMATS.mediumTime;
    locale.DATETIME_FORMATS.shortTime = localeFormats.DATETIME_FORMATS.shortTime;
    locale.DATETIME_FORMATS.shortDate = localeFormats.DATETIME_FORMATS.shortDate;
    locale.NUMBER_FORMATS.DECIMAL_SEP = localeFormats.NUMBER_FORMATS.DECIMAL_SEP;
    locale.NUMBER_FORMATS.number_regex = localeFormats.NUMBER_FORMATS.number_regex;
    locale.NUMBER_FORMATS.CURRENCY_SYM = localeFormats.NUMBER_FORMATS.CURRENCY_SYM;
    locale.NUMBER_FORMATS.GROUP_SEP = localeFormats.NUMBER_FORMATS.GROUP_SEP;
    locale.NUMBER_FORMATS.number_format = localeFormats.NUMBER_FORMATS.number_format;
    fireLocalChangeEvent();
}
export function resetCurrentTranslations() {
    getI18nObject().messageKeys = {};
}
export function getI18nObject() {
    if (window.psMfeUtilI18nObject == null) {
        const locale = {
            'LOCALE': 'en_US',
            'DATETIME_FORMATS': {
                'MONTH': [],
                'SHORTMONTH': [],
                'medium': 'MM/dd/yyyy hh:mm:ss a',
                'mediumDateText': 'MM/DD/YYYY',
                'shortDateText': 'MM/DD/YY',
                'AMPMS': [],
                'mediumDate': 'MM/dd/yyyy',
                'mediumTime': 'hh:mm:ss a',
                'shortTime': 'hh:mm a',
                'short': 'M/d/yy hh:mm a',
                'SHORTDAY': [],
                'DAY': [],
                'shortDate': 'M/d/yy'
            },
            '$BREACH_MITIGATION': 'XRFmEENA55t627cN',
            'NUMBER_FORMATS': {
                'DECIMAL_SEP': '.',
                'number_regex': '([-]?(([0-9]{1,3}(,[0-9]{3})*(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))',
                'CURRENCY_SYM': '$',
                'GROUP_SEP': ',',
                'number_format': '#,###.#########'
            }
        };
        Object.defineProperties(locale.DATETIME_FORMATS.MONTH, {
            0: {
                get: function () {
                    return translate('mfe-utils|month|January');
                }
            },
            1: {
                get: function () {
                    return translate('mfe-utils|month|February');
                }
            },
            2: {
                get: function () {
                    return translate('mfe-utils|month|March');
                }
            },
            3: {
                get: function () {
                    return translate('mfe-utils|month|April');
                }
            },
            4: {
                get: function () {
                    return translate('mfe-utils|month|May');
                }
            },
            5: {
                get: function () {
                    return translate('mfe-utils|month|June');
                }
            },
            6: {
                get: function () {
                    return translate('mfe-utils|month|July');
                }
            },
            7: {
                get: function () {
                    return translate('mfe-utils|month|August');
                }
            },
            8: {
                get: function () {
                    return translate('mfe-utils|month|September');
                }
            },
            9: {
                get: function () {
                    return translate('mfe-utils|month|October');
                }
            },
            10: {
                get: function () {
                    return translate('mfe-utils|month|November');
                }
            },
            11: {
                get: function () {
                    return translate('mfe-utils|month|December');
                }
            }
        });
        Object.defineProperties(locale.DATETIME_FORMATS.SHORTMONTH, {
            0: {
                get: function () {
                    return translate('mfe-utils|short-month|Jan');
                }
            },
            1: {
                get: function () {
                    return translate('mfe-utils|short-month|Feb');
                }
            },
            2: {
                get: function () {
                    return translate('mfe-utils|short-month|Mar');
                }
            },
            3: {
                get: function () {
                    return translate('mfe-utils|short-month|Apr');
                }
            },
            4: {
                get: function () {
                    return translate('mfe-utils|short-month|May');
                }
            },
            5: {
                get: function () {
                    return translate('mfe-utils|short-month|Jun');
                }
            },
            6: {
                get: function () {
                    return translate('mfe-utils|short-month|Jul');
                }
            },
            7: {
                get: function () {
                    return translate('mfe-utils|short-month|Aug');
                }
            },
            8: {
                get: function () {
                    return translate('mfe-utils|short-month|Sep');
                }
            },
            9: {
                get: function () {
                    return translate('mfe-utils|short-month|Oct');
                }
            },
            10: {
                get: function () {
                    return translate('mfe-utils|short-month|Nov');
                }
            },
            11: {
                get: function () {
                    return translate('mfe-utils|short-month|Dec');
                }
            }
        });
        Object.defineProperties(locale.DATETIME_FORMATS.AMPMS, {
            0: {
                get: function () {
                    return translate('mfe-utils|am-pm|AM');
                }
            },
            1: {
                get: function () {
                    return translate('mfe-utils|am-pm|PM');
                }
            }
        });
        Object.defineProperties(locale.DATETIME_FORMATS.SHORTDAY, {
            0: {
                get: function () {
                    return translate('mfe-utils|short-day-of-week|Sun');
                }
            },
            1: {
                get: function () {
                    return translate('mfe-utils|short-day-of-week|Mon');
                }
            },
            2: {
                get: function () {
                    return translate('mfe-utils|short-day-of-week|Tue');
                }
            },
            3: {
                get: function () {
                    return translate('mfe-utils|short-day-of-week|Wed');
                }
            },
            4: {
                get: function () {
                    return translate('mfe-utils|short-day-of-week|Thu');
                }
            },
            5: {
                get: function () {
                    return translate('mfe-utils|short-day-of-week|Fri');
                }
            },
            6: {
                get: function () {
                    return translate('mfe-utils|short-day-of-week|Sat');
                }
            }
        });
        Object.defineProperties(locale.DATETIME_FORMATS.DAY, {
            0: {
                get: function () {
                    return translate('mfe-utils|day-of-week|Sunday');
                }
            },
            1: {
                get: function () {
                    return translate('mfe-utils|day-of-week|Monday');
                }
            },
            2: {
                get: function () {
                    return translate('mfe-utils|day-of-week|Tuesday');
                }
            },
            3: {
                get: function () {
                    return translate('mfe-utils|day-of-week|Wednesday');
                }
            },
            4: {
                get: function () {
                    return translate('mfe-utils|day-of-week|Thursday');
                }
            },
            5: {
                get: function () {
                    return translate('mfe-utils|day-of-week|Friday');
                }
            },
            6: {
                get: function () {
                    return translate('mfe-utils|day-of-week|Saturday');
                }
            }
        });
        window.psMfeUtilI18nObject = {
            //Needed to add the default message key for date at time because it has arguments.
            //This ensures that anyone using the formatDate function will at least get an English translation
            //unless it's overridden by the process.
            messageKeys: {},
            updateCallbacks: [],
            missingMessageKeys: new Set(),
            listMissingMessageKeys: function () {
                console.log(Array.from(this.missingMessageKeys.values()));
            },
            locale: locale
        };
        //We use the setTranslations function here so that the pull-translations script will know which message keys to put into the json file.
        //Any new message keys that have parameters should be added here.
        setTranslations({
            'mfe-utils|date utils|xx at xx': '{{theDate}} at {{theTime}}'
        });
    }
    return window.psMfeUtilI18nObject;
}
export function fireTranslationsChangeEvent() {
    fireI18nChangeEvent('TRANSLATIONS_CHANGE');
}
export function fireLocalChangeEvent() {
    fireI18nChangeEvent('LOCALE_CHANGE');
}
/**
 *
 * @param type 'LOCALE_CHANGE' | 'TRANSLATIONS_CHANGE'
 */
function fireI18nChangeEvent(type) {
    const i18n = getI18nObject();
    for (const callback of i18n.updateCallbacks) {
        try {
            //Wrapped in a try catch block just in case there are errors
            //We still need to update the rest and we don't want to crash
            //the frontend application just because someone forgot to remove their change listener.
            callback(type);
        }
        catch (e) {
            console.error(e);
        }
    }
}
/**
 *
 * @param callback A Callback function that will be called whenever the message translations change.
 * @returns An unsubscribe function.
 */
export function addTranslationListener(callback) {
    return addUpdateListener(callback, 'TRANSLATIONS_CHANGE');
}
export function addLocalListener(callback) {
    return addUpdateListener(callback, 'LOCALE_CHANGE');
}
function addUpdateListener(callback, type) {
    const i18n = getI18nObject();
    const callbackWrapper = (callbackType) => {
        if (callbackType == null || callbackType === type) {
            callback();
        }
    };
    i18n.updateCallbacks.push(callbackWrapper);
    return () => {
        const index = i18n.updateCallbacks.indexOf(callbackWrapper);
        if (index >= 0) {
            i18n.updateCallbacks.splice(index, 1);
        }
    };
}
export function keyExists(key) {
    return getCurrentTranslations()[key] != null;
}
/**
 * Returns the translated message for the given message key.
 * NOTE: You must set message keys prior to the translate function being able to work correctly.
 *
 * @example Key: 'mfe-1|Course': 'Course' -> translate('mfe-1|Course'); //returns 'Course'
 * @example Key/Value pair: 'neon|grid-widget|page number': 'Page Number {{pageNumber}}'
 * translate('neon|grid-widget|page number', 10) //returns Page Number 10
 * @example Key/Value pair: 'neon|demographics|address':'My Age: {{age}} My Id: {{id}} Zip: {{zipcode}}'
 * translate{'neon|demographics|address', {age: '42', zipcode: '95621', id: '154548'}); returns 'My Age: 42 My Id: 154548 Zip: 95621'
 *
 * @param key The message key to translate. If you pass nothing into this function it will return an empty string. If no message key has been set,
 * it will give you everything after the last pipe character in the message key.
 * @param params If there are double curly braces around parameters in your message's translated value,
 * the params value or map will be used to realize them.
 * @returns A translated message string.
 */
export function translate(key, params) {
    if (typeof key !== 'string') {
        return '';
    }
    const msgKeys = getCurrentTranslations();
    let msg = msgKeys[key];
    if (msg == null) {
        getI18nObject().missingMessageKeys.add(key);
        msg = stripContextFromKey(key);
    }
    if (msg == null) {
        msg = key;
    }
    if (params != null) {
        if (typeof params === 'string' ||
            typeof params === 'boolean' ||
            typeof params === 'number') {
            msg = msg.replace(ARGUMENT_REGEX, String(params));
        }
        else {
            for (const k of Object.keys(params)) {
                const regex = new RegExp('\\{\\{' + k + '}}', 'g');
                msg = msg.replace(regex, params[k]);
            }
        }
    }
    return msg;
}
export function stripContextFromKey(key) {
    const result = PIPE_REGEX.exec(key);
    if (result != null && result.length > 1) {
        return result[1];
    }
    return key;
}
/**
   * @param date: The date object to format
   * @param useMonthAbbreviations: Set to false for a date the screen readers can read nicely.
   * @param isMidSentence: Set this to true if the output will be inserted in the middle of a sentence.
   * @returns String: The formatted date string.
   */
function localizeDatePretty(date, useMonthAbbreviations = true, isMidSentence = false) {
    const locale = getCurrentLocale();
    if (!(date instanceof Date)) {
        return '';
    }
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    if (now.getDate() === date.getDate() &&
        now.getMonth() === date.getMonth() &&
        now.getFullYear() === date.getFullYear()) {
        return isMidSentence ? translate('global|today') : translate('global|Today');
    }
    else if (tomorrow.getDate() === date.getDate() &&
        tomorrow.getMonth() === date.getMonth() &&
        tomorrow.getFullYear() === date.getFullYear()) {
        return isMidSentence ? translate('global|tomorrow') : translate('global|Tomorrow');
    }
    else if (yesterday.getDate() === date.getDate() &&
        yesterday.getMonth() === date.getMonth() &&
        yesterday.getFullYear() === date.getFullYear()) {
        return isMidSentence ? translate('global|yesterday') : translate('global|Yesterday');
    }
    let dateString;
    const dateFormat = locale.DATETIME_FORMATS.mediumDate.toLowerCase();
    const startType = dateFormat.charAt(0); //'y' | 'm' | 'd'
    const endType = dateFormat.charAt(dateFormat.length - 1);
    const useYear = now.getFullYear() !== date.getFullYear();
    let month;
    const day = date.getDate();
    const year = date.getFullYear();
    if (useMonthAbbreviations) {
        month = locale.DATETIME_FORMATS.SHORTMONTH[date.getMonth()];
    }
    else {
        month = locale.DATETIME_FORMATS.MONTH[date.getMonth()];
    }
    switch (startType) {
        case 'd':
            if (endType === 'y') {
                //d m y
                dateString = day + ' ' + month;
                if (useYear) {
                    dateString += ' ' + year;
                }
            }
            else {
                //d, y m
                if (useYear) {
                    dateString = day + ', ' + year + ' ' + month;
                }
                else {
                    dateString = day + ' ' + month;
                }
            }
            break;
        default:
        case 'm':
            if (endType === 'y') {
                //m d, y
                dateString = month + ' ' + day;
                if (useYear) {
                    dateString += ', ' + year;
                }
            }
            else {
                //m y, d
                if (useYear) {
                    dateString = month + ' ' + year + ', ' + day;
                }
                else {
                    dateString = month + ' ' + day;
                }
            }
            break;
        case 'y':
            if (useYear) {
                if (endType === 'm') {
                    //y, d m
                    dateString = year + ', ' + day + ' ' + month;
                }
                else {
                    //y m d
                    dateString = year + ' ' + month + ' ' + day;
                }
            }
            else {
                if (endType === 'm') {
                    //d m
                    dateString = day + ' ' + month;
                }
                else {
                    //m d
                    dateString = month + ' ' + day;
                }
            }
            break;
    }
    return dateString;
}
export function localizeDate(d, mmDdOnly) {
    const locale = getCurrentLocale();
    if (d == null) {
        return '';
    }
    if (!isValidDateObject(d)) {
        return String(d);
    }
    const separatorMatch = locale.DATETIME_FORMATS.mediumDate.match(/[^A-Za-z]/);
    let separator = '/';
    if (separatorMatch != null) {
        separator = separatorMatch[0];
    }
    const patternPieces = locale.DATETIME_FORMATS.mediumDate.split(separator);
    const localizedStrings = [];
    for (let i = 0; i < 3; i++) {
        switch (patternPieces[i]) {
            case 'd':
                localizedStrings.push(d.getDate());
                break;
            case 'dd':
                localizedStrings.push(d.getDate());
                break;
            case 'M':
                localizedStrings.push(d.getMonth() + 1);
                break;
            case 'MM':
                localizedStrings.push(d.getMonth() + 1);
                break;
            case 'yy':
                if (mmDdOnly !== true) {
                    localizedStrings.push(d.getFullYear());
                }
                break;
            case 'yyyy':
                if (mmDdOnly !== true) {
                    localizedStrings.push(d.getFullYear());
                }
                break;
            default:
                console.error('Unexpected format while localizing date');
        }
    }
    return localizedStrings.join(separator);
}
export function deLocalizeDate(localizedDateString) {
    if (localizedDateString == null || localizedDateString === '') {
        return;
    }
    const locale = getCurrentLocale();
    const separatorMatch = locale.DATETIME_FORMATS.mediumDate.match(/[^A-Za-z]/);
    let separator = '/';
    if (separatorMatch != null) {
        separator = separatorMatch[0];
    }
    const patternPieces = locale.DATETIME_FORMATS.mediumDate.split(separator);
    const localizedPieces = localizedDateString.split(separator);
    if (localizedPieces.length === 3) {
        let day;
        let month;
        let year;
        for (let i = 0; i < 3; i++) {
            switch (patternPieces[i]) {
                case 'd':
                case 'dd':
                    day = localizedPieces[i];
                    break;
                case 'M':
                case 'MM':
                    month = localizedPieces[i];
                    break;
                case 'yy':
                case 'yyyy':
                    year = localizedPieces[i];
                    if (TWO_DIGIT_REGEX.test(year)) {
                        if (parseInt(year) > 50) {
                            year = '19' + year;
                        }
                        else {
                            year = '20' + year;
                        }
                    }
                    break;
                default:
                    console.error('Unexpected format when delocalizing date.');
            }
        }
        //  /.-,
        const d = new Date(year, month - 1, day, 0, 0, 0);
        if (isNaN(year) || isNaN(month) || isNaN(day) ||
            d.getFullYear() !== Number(year) ||
            d.getMonth() !== Number(month - 1) ||
            d.getDate() !== Number(day) ||
            d.getFullYear() < 0) {
            return;
        }
        if (isValidDateObject(d)) {
            return d;
        }
    }
}
/**
 * @param d - a javascript date object
 */
export function isValidDateObject(d) {
    if (d instanceof Date) {
        return !isNaN(d.getTime()); //invalid date object will return NaN for getTime
    }
    return false;
}
/**
 * Is the localized date string a valid date?
 * Note: In PDS, a '' string would return true but it is changed in mfe-utils.
 * '' is NOT a valid date.
 * @param localizedDate a date string.
 * @returns {any}
 */
export function isValidDate(localizedDate) {
    const parsedDate = deLocalizeDate(localizedDate);
    return isValidDateObject(parsedDate);
}
/**
 * @param psJsonDateString - String - in the form of yyyy-mm-dd or yyyy-mm-dd HH:mm:ss
 * @param preserceTimeOfDay - boolean - optional param to retain HH:mm:ss in returned date
 * object. Default is false if not passed.
 * @returns {*}
 */
export function parseJsonDate(psJsonDateString, preserveTimeOfDay) {
    if (psJsonDateString == null) {
        return;
    }
    let year = 0;
    let month = 0;
    let date = 0;
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    const split = psJsonDateString.split('-');
    if (split.length < 3) {
        return;
    }
    //a valid jsonDateString may contain a time of date hour/minute/seccond
    const dayTimeOfDay = split[2].split(' ');
    if (dayTimeOfDay.length === 2) {
        //time of day is present
        split[2] = dayTimeOfDay[0];
        if (preserveTimeOfDay) {
            const timeOfDay = dayTimeOfDay[1].split(':');
            if (timeOfDay.length === 3) {
                hours = parseInt(timeOfDay[0]);
                minutes = parseInt(timeOfDay[1]);
                seconds = parseInt(timeOfDay[2]);
            }
            //else invalid HH:mm:ss - ignore it
        }
    }
    year = parseInt(split[0]);
    month = parseInt(split[1]) - 1;
    date = parseInt(split[2]);
    const returnDate = new Date(year, month, date, hours, minutes, seconds);
    if (isNaN(returnDate.getTime())) {
        return;
    }
    return returnDate;
}
/**
 * Sister function of parseJsonDate.  Converts a date object into the JsonDateString format
 * necessary for XTE.
 */
export function getJsonDate(javascriptDateObject, preserveTimeOfDay) {
    if (javascriptDateObject == null || !(javascriptDateObject instanceof Date)) {
        return;
    }
    if (isNaN(javascriptDateObject.getTime())) {
        return;
    }
    let psJsonDateString = javascriptDateObject.getFullYear() + '-' +
        padZeros((javascriptDateObject.getMonth() + 1), 2) + '-' +
        padZeros(javascriptDateObject.getDate(), 2);
    if (preserveTimeOfDay) {
        psJsonDateString += ' ' + padZeros(javascriptDateObject.getHours(), 2) + ':' +
            padZeros(javascriptDateObject.getMinutes(), 2) + ':' +
            padZeros(javascriptDateObject.getSeconds(), 2);
    }
    return psJsonDateString;
}
/**
 * Converts a JSON date to a non localized date
 * @param psJsonDateString - String - in the form of yyyy-mm-dd
 * @returns - String in the form of MM/DD/YYYY
 */
export function parseJsonDateToNonLocalized(psJsonDateString) {
    const date = parseJsonDate(psJsonDateString);
    if (date != null) {
        return getMmDdYyyyDateString(date);
    }
    return psJsonDateString;
}
/**
 * Gets the date in the form MM/DD/YYYY
 * @param date The date object to convert.
 */
export function getMmDdYyyyDateString(date) {
    return (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
}
export function addDaysToDate(date, numDays) {
    date.setDate(date.getDate() + numDays);
    return date;
}
export function getLocalizedDayOfWeek(date) {
    const locale = getCurrentLocale();
    return locale.DATETIME_FORMATS.DAY[date.getDay()];
}
/**
 * Generate a localized time string from a javascript date object.
 * @param time - javascript date object
 * @returns {string}
 * compact variable is to display time more compact 11:00 AM becomes 11am -- 11:30 PM becomes 11:30pm
 * compact is overwritten if show24Hours is true.
 */
export function localizeTime(time, padWithZero = false, compact = false, includeSeconds = false) {
    if (time == null) {
        return;
    }
    const locale = getCurrentLocale();
    const separator = ':';
    const ampmNames = locale.DATETIME_FORMATS.AMPMS;
    let show24Hours = false;
    if (includeSeconds === true) {
        show24Hours = locale.DATETIME_FORMATS.mediumTime.toLowerCase() === 'hh:mm:ss';
    }
    else {
        show24Hours = locale.DATETIME_FORMATS.shortTime.toLowerCase() === 'hh:mm';
    }
    let hours = time.getHours();
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();
    let hoursString;
    let minutesString;
    let secondsString;
    if (show24Hours) {
        padWithZero = true;
        compact = false;
    }
    else {
        hours = ((hours + 11) % 12) + 1;
    }
    if (padWithZero && hours < 10 && !compact) {
        hoursString = '0' + hours;
    }
    else {
        hoursString = String(hours);
    }
    if (minutes < 10) {
        minutesString = '0' + minutes;
    }
    else {
        minutesString = String(minutes);
    }
    if (includeSeconds) {
        if (seconds < 10) {
            secondsString = '0' + seconds;
        }
        else {
            secondsString = String(seconds);
        }
        if (compact) {
            if (minutes === 0 && seconds === 0) {
                return hoursString + ampmNames[time.getHours() < 12 ? 0 : 1].toLowerCase();
            }
            else if (seconds === 0) {
                return hoursString + separator + minutesString + ampmNames[time.getHours() < 12 ? 0 : 1].toLowerCase();
            }
            else {
                return hoursString + separator + minutesString + separator + minutesString + ampmNames[time.getHours() < 12 ? 0 : 1].toLowerCase();
            }
        }
        else {
            return hoursString + separator + minutesString + separator + secondsString + (show24Hours ? '' : ' ' + ampmNames[time.getHours() < 12 ? 0 : 1]);
        }
    }
    else {
        if (compact) {
            if (minutes === 0) {
                return hoursString + ampmNames[time.getHours() < 12 ? 0 : 1].toLowerCase();
            }
            else {
                return hoursString + separator + minutesString + ampmNames[time.getHours() < 12 ? 0 : 1].toLowerCase();
            }
        }
        else {
            return hoursString + separator + minutesString + (show24Hours ? '' : ' ' + ampmNames[time.getHours() < 12 ? 0 : 1]);
        }
    }
}
// time = HH:mm | HH:mm:ss | hh:mm a | hh:mm:ss a
export function deLocalizeTime(time) {
    if (time == null) {
        return;
    }
    const locale = getCurrentLocale();
    let hours;
    let minutes;
    let seconds;
    let timeTokens;
    if (locale.DATETIME_FORMATS.shortTime.endsWith('a')) {
        //AM PM time
        timeTokens = AM_PM_TIME_REGEX.exec(time);
        if (timeTokens == null || timeTokens.length < 6 || !isLocalizedAmPm(timeTokens[5])) {
            return;
        }
        hours = Number(timeTokens[1]);
        minutes = Number(timeTokens[2]);
        seconds = Number(timeTokens[3]);
        if (isLocalizedPm(timeTokens[5])) {
            if (hours < 12) {
                hours += 12;
            }
        }
        else {
            // AM
            if (hours === 12) {
                hours = 0;
            }
        }
    }
    else {
        //24 hour time
        return parse24HourTime(time);
    }
    return new SimpleTime(hours, minutes, seconds);
}
export function parse24HourTime(time) {
    if (time == null) {
        return;
    }
    const timeTokens = time.split(':');
    // Tokens should be the hours and minutes, with optional seconds
    if (timeTokens.length < 2 || timeTokens.length > 3) {
        return;
    }
    const hours = Number(timeTokens[0]);
    const minutes = Number(timeTokens[1]);
    const seconds = Number(timeTokens[2] || '0');
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        return;
    }
    return new SimpleTime(hours, minutes, seconds);
}
export function isValidTime(localizedTime) {
    const locale = getCurrentLocale();
    const show24Hours = locale.DATETIME_FORMATS.shortTime.toLowerCase() === 'hh:mm' || locale.DATETIME_FORMATS.shortTime.toLowerCase() === 'hh:mm:ss';
    if (show24Hours) {
        return TWENTY_FOUR_HOUR_TIME_REGEX.test(localizedTime);
    }
    else {
        //parse the localizedTime into tokens by executing the regex.
        //When this regex returns a RexExpExecArray with length === 6, it validates that the time is in the correct format
        //except for the AM/PM form which will be validated below in the isLocalizedAmPm() function
        const result = AM_PM_TIME_REGEX.exec(localizedTime);
        //result == null means the entire regex failed.
        //result.length > 5 is a guard to make sure we can access result[5]
        //result[5] contains what the user typed for the am/pm part so we ensure that it is a valid localized am/pm string.
        return result != null && result.length > 5 && isLocalizedAmPm(result[5]);
    }
}
//Returns true if the string matches the translated AM or PM tokens.
//Note: The comparison is done with all spaces stripped out and lowerCase.
//This allows the user to type in things like 'A m' and have it still work
function isLocalizedAmPm(amPmString) {
    return isLocalizedPm(amPmString) || isLocalizedAm(amPmString);
}
//Returns true if the string matches the translated PM token.
//Note: The comparison is done with all spaces stripped out and lowerCase.
//This allows the user to type in things like 'P m' and have it still work
function isLocalizedPm(pmString) {
    if (typeof pmString !== 'string') {
        return false;
    }
    return pmString.replace(/ /g, '').toLowerCase() === translate('mfe-utils|am-pm|PM').replace(/ /g, '').toLowerCase();
}
//Returns true if the string matches the translated AM token.
//Note: The comparison is done with all spaces stripped out and lowerCase.
//This allows the user to type in things like 'A m' and have it still work
function isLocalizedAm(pmString) {
    if (typeof pmString !== 'string') {
        return false;
    }
    return pmString.replace(/ /g, '').toLowerCase() === translate('mfe-utils|am-pm|AM').replace(/ /g, '').toLowerCase();
}
/**
 * Localize a number, and optionally round it to a specific number of decimal places
 * @param number, number to localize
 * @param decimalPlaces (optional), number of decimal places to round to
 * @param strictDecimals (optional), appends zeroes if number has fewer decimal places
 * @returns the localized version of the number
 */
export function localizeNumber(number, decimalPlaces, strictDecimals, useGroupSeparator) {
    if (number == null || isNaN(number)) {
        return;
    }
    const locale = getCurrentLocale();
    if (decimalPlaces != null && !isNaN(decimalPlaces)) {
        // round the number to n decimal places before localizing
        decimalPlaces = Math.abs(Math.floor(decimalPlaces));
        const multiplier = Math.pow(10, decimalPlaces);
        number = Math.round(number * multiplier) / multiplier;
    }
    let formattedNumber = String(number).replace('.', locale.NUMBER_FORMATS.DECIMAL_SEP);
    if (strictDecimals === true && decimalPlaces != null && !isNaN(decimalPlaces) && decimalPlaces > 0) {
        //Add zeroes and an appropriate decimal separator as padding to the localized number
        let numDecimals = 0;
        if (formattedNumber.includes(locale.NUMBER_FORMATS.DECIMAL_SEP)) {
            const parts = String(formattedNumber).split(locale.NUMBER_FORMATS.DECIMAL_SEP);
            numDecimals = parts[parts.length - 1].length;
        }
        if (numDecimals === 0) {
            formattedNumber = formattedNumber + locale.NUMBER_FORMATS.DECIMAL_SEP;
        }
        formattedNumber = formattedNumber + ('0').repeat(decimalPlaces - numDecimals);
    }
    if (useGroupSeparator && locale.NUMBER_FORMATS.GROUP_SEP.length > 0) {
        const [wholePart, decimalPart] = formattedNumber.split(locale.NUMBER_FORMATS.DECIMAL_SEP);
        formattedNumber = injectGroupSeparators(wholePart) + (decimalPart != null ? locale.NUMBER_FORMATS.DECIMAL_SEP + decimalPart : '');
    }
    return formattedNumber;
}
//The number must be a string of ONLY number characters, no decimal points
function injectGroupSeparators(number) {
    const locale = getCurrentLocale();
    const separatorLength = locale.NUMBER_FORMATS.number_format.indexOf(locale.NUMBER_FORMATS.DECIMAL_SEP) - locale.NUMBER_FORMATS.number_format.indexOf(locale.NUMBER_FORMATS.GROUP_SEP) - 1;
    let stringBuffer = '';
    let count = 0; //How many numeric characters we have added into the string buffer
    for (let i = number.length - 1; i >= 0; i--) {
        //First prepend the character
        stringBuffer = number[i] + stringBuffer;
        count++;
        //Now prepend the separatorChar if necessary
        if (count > 0 && count % separatorLength === 0 && i !== 0) {
            stringBuffer = locale.NUMBER_FORMATS.GROUP_SEP + stringBuffer;
        }
    }
    return stringBuffer;
}
export function deLocalizeNumber(numberString) {
    if (numberString == null) {
        return;
    }
    const locale = getCurrentLocale();
    const groupingSeparator = locale.NUMBER_FORMATS.GROUP_SEP;
    const decimalSeparator = locale.NUMBER_FORMATS.DECIMAL_SEP;
    const numericRegex = locale.NUMBER_FORMATS.number_regex;
    // IE does not like instantiating RegExp objects with empty values, so we have to
    // check for valid values in the grouping separator before using it in a RegExp.
    const gsNotBlank = (groupingSeparator === '.' || groupingSeparator === ',' || groupingSeparator === ' ');
    // We also want to check that only valid characters are present.
    const validNumber = new RegExp('^' + numericRegex + '$', 'g');
    if (numberString !== '' && numberString.match(validNumber) != null) {
        if (gsNotBlank) {
            const gs = new RegExp('\\' + groupingSeparator, 'g');
            numberString = numberString.replace(gs, '');
        }
        const ds = new RegExp('\\' + decimalSeparator, 'g'); //The decimal separator is not expected to be empty
        const number = parseFloat(numberString.replace(ds, '.'));
        if (!isNaN(number)) {
            return number;
        }
    }
}
export function isValidNumber(numValue) {
    if (numValue !== '') {
        const locale = getCurrentLocale();
        const regexString = '^' + locale.NUMBER_FORMATS.number_regex + '$';
        const regex = new RegExp(regexString, 'g');
        if (regex.test(numValue)) {
            return (deLocalizeNumber(numValue) != null);
        }
        else {
            return false;
        }
    }
    return false;
}
//TODO: Add unit tests
/**
 * Creates a new Date object with the same day, month, and year of the passed in Date combined with the hours and minutes
 * of the time object.
 * @param date
 * @param time
 * @returns {Date} - A brand new date object.
 */
export function combineDateAndTime(date, time) {
    if (time == null) {
        time = new SimpleTime(0, 0);
    }
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes());
}
//TODO: Add unit tests
/**
 * Creates a time object based on the hours and minutes in the date object.
 * @param date
 * @returns Time
 */
export function extractTime(date) {
    return new SimpleTime(date.getHours(), date.getMinutes());
}
/**
 * This is the function to format a time for display.
 */
export function formatTime(time, formatType) {
    if (time == null || formatType == null) {
        return '';
    }
    switch (formatType) {
        case 'TIME':
            return localizeTime(time, false);
        case 'TIME_PAD_ZEROS':
            return localizeTime(time, true);
        case 'TIME_COMPACT':
            return localizeTime(time, false, true);
        case 'TIME_SECONDS':
            return localizeTime(time, false, false, true);
        case 'TIME_SECONDS_PAD_ZEROS':
            return localizeTime(time, true, false, true);
        case 'TIME_SECONDS_COMPACT':
            return localizeTime(time, false, true, true);
        default:
            return localizeTime(time, false);
    }
}
//TODO: Look into hiding all of the other functions that have to do with localizing a date in order to make this function here the way to go.
//TODO: Need to add better documentation for this function.
/**
 * This is the function to format a date for display.
 * @param date: The date object to format.
 * @param formatType: The format that you want to use.
 * @param isMidSentence: Set this to true if the output will be inserted in the middle of a sentence.
 */
export function formatDate(date, formatType, isMidSentence = false) {
    if (date == null) {
        return '';
    }
    switch (formatType) {
        case 'DATE_TIME':
            return translate('mfe-utils|date utils|xx at xx', {
                theDate: localizeDate(date),
                theTime: localizeTime(date)
            });
        case 'DATE_TIME_SR':
            return translate('mfe-utils|date utils|xx at xx', {
                theDate: localizeDatePretty(date, false, isMidSentence),
                theTime: localizeTime(date, false)
            });
        case 'DATE_TIME_PRETTY':
            return translate('mfe-utils|date utils|xx at xx', {
                theDate: localizeDatePretty(date, true, isMidSentence),
                theTime: localizeTime(date, false)
            });
        case 'DATE':
            return localizeDate(date);
        case 'DATE_SR':
            return localizeDatePretty(date, false, isMidSentence);
        case 'DATE_DAY_SHORT_MONTH':
            return dateDayShortMonth(date);
        case 'DATE_SHORT_MONTH':
            return dateShortMonth(date);
        case 'DATE_SHORT_DAY_SHORT_MONTH':
            return dateShortDayShortMonth(date);
        case 'DATE_PRETTY':
        default:
            return localizeDatePretty(date, true, isMidSentence);
    }
}
/**
 * Checks if the date format in the provided locale uses a "day before month" format.
 * This function checks the 'mediumDate' format in the locale to determine the format.
 * @param locale - The locale for which you want to check the date format.
 * @returns True if the format is "day before month," otherwise false.
 */
function isDayBeforeMonthFormat(locale) {
    return /d[^dm]*M/i.test(locale.DATETIME_FORMATS.mediumDate);
}
function dateDayShortMonth(date) {
    const locale = getCurrentLocale();
    const dayName = locale.DATETIME_FORMATS.DAY[date.getDay()];
    const day = date.getDate();
    const monthName = locale.DATETIME_FORMATS.SHORTMONTH[date.getMonth()];
    if (isDayBeforeMonthFormat(locale)) {
        return `${dayName}, ${day} ${monthName}`;
    }
    else {
        return `${dayName}, ${monthName} ${day}`;
    }
}
function dateShortMonth(date) {
    const locale = getCurrentLocale();
    const day = date.getDate();
    const monthName = locale.DATETIME_FORMATS.SHORTMONTH[date.getMonth()];
    if (isDayBeforeMonthFormat(locale)) {
        return `${day}, ${monthName}`;
    }
    else {
        return `${monthName}, ${day}`;
    }
}
function dateShortDayShortMonth(date) {
    const locale = getCurrentLocale();
    const day = date.getDate();
    const monthName = locale.DATETIME_FORMATS.SHORTMONTH[date.getMonth()];
    const dayName = locale.DATETIME_FORMATS.SHORTDAY[date.getDay()];
    if (isDayBeforeMonthFormat(locale)) {
        return `${dayName}, ${day} ${monthName}`;
    }
    else {
        return `${dayName}, ${monthName} ${day}`;
    }
}
/**
 * Formats the given number based on the current locale.
 * @param number: The number to format.
 * @param decimalPlaces: The maximum number of decimal places to allow.
 * @param strictDecimals: When set to true, if decimalPlaces is 2, there will always be 2 decimal places, regardless if they are both zeroes.
 * @param useGroupSeparator: When set to true, group separators will be included in the format.  Ex: the comma in 1,000 is the group separator.
 * @returns A localized version of the number.
 */
export function formatNumber(number, decimalPlaces, strictDecimals = false, useGroupSeparator) {
    return localizeNumber(number, decimalPlaces, strictDecimals, useGroupSeparator);
}
//Private function
function padZeros(number, padding) {
    let paddedString = '';
    if (isNaN(number) || isNaN(padding) || number == null || padding == null) {
        if (number == null) {
            return;
        }
        return String(number);
    }
    else {
        paddedString = number.toString();
        while (paddedString.length < padding) {
            paddedString = '0' + paddedString;
        }
        return paddedString;
    }
}
export function setNumberFormat(numberFormat) {
    const numberFormats = getCurrentLocale().NUMBER_FORMATS;
    numberFormats.number_format = numberFormat;
    /* eslint-disable no-fallthrough */
    switch (numberFormat) {
        default:
            //If we hit default, that means that the passed in number_format that wasn't valid, so we have to overwrite it to the default.
            numberFormats.number_format = '#,###.##';
        case '#,###.##':
            numberFormats.GROUP_SEP = ',';
            numberFormats.DECIMAL_SEP = '.';
            numberFormats.number_regex = '([-]?(([0-9]{1,3}(,[0-9]{3})*(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))';
            break;
        case '#,####.##':
            numberFormats.GROUP_SEP = ',';
            numberFormats.DECIMAL_SEP = '.';
            numberFormats.number_regex = '([-]?(([0-9]{1,4}(,[0-9]{4})*(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))';
            break;
        case '#,###0.00':
            numberFormats.GROUP_SEP = ',';
            numberFormats.DECIMAL_SEP = '.';
            numberFormats.number_regex = '([-]?(([0-9]{1,4}(,[0-9]{4})*(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))';
            break;
        case '#,##0.00':
            numberFormats.GROUP_SEP = ',';
            numberFormats.DECIMAL_SEP = '.';
            numberFormats.number_regex = '([-]?(([0-9]{1,3}(,[0-9]{3})*(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))'; //done
            break;
        case '#.###,##':
            numberFormats.GROUP_SEP = '.';
            numberFormats.DECIMAL_SEP = ',';
            numberFormats.number_regex = '([-]?(([0-9]{1,3}(\\.[0-9]{3})*(,[0-9]+)?|,[0-9]+)|([0-9]*(,[0-9]+)?)))'; //done
            break;
        case '#.##0,00':
            numberFormats.GROUP_SEP = '.';
            numberFormats.DECIMAL_SEP = ',';
            numberFormats.number_regex = '([-]?(([0-9]{1,3}(\\.[0-9]{3})*(,[0-9]+)?|,[0-9]+)|([0-9]*(,[0-9]+)?)))'; //done
            break;
        case '####,##':
            numberFormats.GROUP_SEP = '';
            numberFormats.DECIMAL_SEP = ',';
            numberFormats.number_regex = '([-]?(([0-9]+(,[0-9]+)?|,[0-9]+)|([0-9]*(,[0-9]+)?)))'; //done
            break;
        case '# ###,##':
            numberFormats.GROUP_SEP = ' ';
            numberFormats.DECIMAL_SEP = ',';
            numberFormats.number_regex = '([-]?(([0-9]{1,3}( [0-9]{3})*(,[0-9]+)?|,[0-9]+)|([0-9]*(,[0-9]+)?)))'; //done
            break;
        case '####.##':
            numberFormats.GROUP_SEP = '';
            numberFormats.DECIMAL_SEP = '.';
            numberFormats.number_regex = '([-]?(([0-9]+(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))'; //done
            break;
        case '# ###.##':
            numberFormats.GROUP_SEP = ' ';
            numberFormats.DECIMAL_SEP = '.';
            numberFormats.number_regex = '([-]?(([0-9]{1,3}( [0-9]{3})*(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))'; //done
            break;
        case '###0,00':
            numberFormats.GROUP_SEP = '';
            numberFormats.DECIMAL_SEP = ',';
            numberFormats.number_regex = '([-]?(([0-9]+(,[0-9]+)?|,[0-9]+)|([0-9]*(,[0-9]+)?)))'; //done
            break;
        case '# ##0,00':
            numberFormats.GROUP_SEP = ' ';
            numberFormats.DECIMAL_SEP = ',';
            numberFormats.number_regex = '([-]?(([0-9]{1,3}( [0-9]{3})*(,[0-9]+)?|,[0-9]+)|([0-9]*(,[0-9]+)?)))'; //done
            break;
        case '###0.00':
            numberFormats.GROUP_SEP = '';
            numberFormats.DECIMAL_SEP = '.';
            numberFormats.number_regex = '([-]?(([0-9]+(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))'; //done
            break;
        case '# ##0.00':
            numberFormats.GROUP_SEP = ' ';
            numberFormats.DECIMAL_SEP = '.';
            numberFormats.number_regex = '([-]?(([0-9]{1,3}( [0-9]{3})*(\\.[0-9]+)?|\\.[0-9]+)|([0-9]*(\\.[0-9]+)?)))'; //done
            break;
    }
    /* eslint-enable no-fallthrough */
}
